/**
 * 移动端Horizontal thumbnail carousel功能
 * 实现触摸滑动、点击交互等功能
 */

class ComplementaryMobileCarousel {
  constructor() {
    this.init();
  }

  init() {
    this.bindEvents();
    this.setupTouchEvents();
    this.setupScrollIndicators();
  }

  bindEvents() {
    // 监听DOM变化，确保动态加载的内容也能被处理
    if (typeof MutationObserver !== "undefined") {
      const observer = new MutationObserver(() => {
        this.setupTouchEvents();
        this.setupScrollIndicators();
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }

    // 监听窗口大小变化
    window.addEventListener("resize", this.handleResize.bind(this));

    // 监听触摸事件
    this.setupTouchEvents();
  }

  setupTouchEvents() {
    const carousels = document.querySelectorAll(
      ".complementary-thumbnail-carousel"
    );

    carousels.forEach((carousel) => {
      const thumbnails = carousel.querySelector(".complementary-thumbnails");
      if (!thumbnails) return;

      let startX = 0;
      let startY = 0;
      let currentX = 0;
      let isDragging = false;
      let startScrollLeft = 0;

      // 触摸开始
      const handleTouchStart = (e) => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        startScrollLeft = thumbnails.scrollLeft;
        isDragging = true;

        // 添加拖动状态类
        thumbnails.classList.add("dragging");

        // 防止默认滚动
        e.preventDefault();
      };

      // 触摸移动
      const handleTouchMove = (e) => {
        if (!isDragging) return;

        currentX = e.touches[0].clientX;
        const diffX = startX - currentX;
        const diffY = Math.abs(startY - e.touches[0].clientY);

        // 如果垂直滑动距离大于水平滑动距离，则不处理
        if (diffY > Math.abs(diffX)) return;

        // 计算新的滚动位置
        const newScrollLeft = startScrollLeft + diffX;
        thumbnails.scrollLeft = newScrollLeft;

        e.preventDefault();
      };

      // 触摸结束
      const handleTouchEnd = (e) => {
        if (!isDragging) return;

        isDragging = false;
        thumbnails.classList.remove("dragging");

        // 计算滑动距离和速度
        const endX = e.changedTouches[0].clientX;
        const diffX = startX - endX;
        const velocity = Math.abs(diffX) / 100; // 简单的速度计算

        // 如果滑动距离足够大，自动滚动到下一个项目
        if (Math.abs(diffX) > 50 && velocity > 0.5) {
          this.snapToNextItem(thumbnails, diffX > 0);
        } else {
          // 否则回弹到最近的snap点
          this.snapToNearest(thumbnails);
        }
      };

      // 鼠标事件（用于桌面端测试）
      const handleMouseDown = (e) => {
        startX = e.clientX;
        startY = e.clientY;
        startScrollLeft = thumbnails.scrollLeft;
        isDragging = true;

        thumbnails.classList.add("dragging");
        e.preventDefault();
      };

      const handleMouseMove = (e) => {
        if (!isDragging) return;

        currentX = e.clientX;
        const diffX = startX - currentX;
        const diffY = Math.abs(startY - e.clientY);

        if (diffY > Math.abs(diffX)) return;

        const newScrollLeft = startScrollLeft + diffX;
        thumbnails.scrollLeft = newScrollLeft;

        e.preventDefault();
      };

      const handleMouseUp = (e) => {
        if (!isDragging) return;

        isDragging = false;
        thumbnails.classList.remove("dragging");

        const endX = e.clientX;
        const diffX = startX - endX;
        const velocity = Math.abs(diffX) / 100;

        if (Math.abs(diffX) > 50 && velocity > 0.5) {
          this.snapToNextItem(thumbnails, diffX > 0);
        } else {
          this.snapToNearest(thumbnails);
        }
      };

      // 绑定触摸事件
      thumbnails.addEventListener("touchstart", handleTouchStart, {
        passive: false,
      });
      thumbnails.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      });
      thumbnails.addEventListener("touchend", handleTouchEnd, {
        passive: false,
      });

      // 绑定鼠标事件
      thumbnails.addEventListener("mousedown", handleMouseDown);
      thumbnails.addEventListener("mousemove", handleMouseMove);
      thumbnails.addEventListener("mouseup", handleMouseUp);
      thumbnails.addEventListener("mouseleave", handleMouseUp);

      // 防止拖拽时选中文本
      thumbnails.addEventListener("selectstart", (e) => {
        if (isDragging) e.preventDefault();
      });
    });
  }

  snapToNextItem(thumbnails, direction) {
    const items = thumbnails.querySelectorAll(".complementary-thumbnail-item");
    if (items.length === 0) return;

    const itemWidth = items[0].offsetWidth + 16; // 包含gap
    const currentScroll = thumbnails.scrollLeft;
    const maxScroll = thumbnails.scrollWidth - thumbnails.clientWidth;

    let targetScroll;

    if (direction) {
      // 向右滑动，显示下一个
      targetScroll = Math.min(currentScroll + itemWidth, maxScroll);
    } else {
      // 向左滑动，显示上一个
      targetScroll = Math.max(currentScroll - itemWidth, 0);
    }

    this.smoothScrollTo(thumbnails, targetScroll);
  }

  snapToNearest(thumbnails) {
    const items = thumbnails.querySelectorAll(".complementary-thumbnail-item");
    if (items.length === 0) return;

    const itemWidth = items[0].offsetWidth + 16;
    const currentScroll = thumbnails.scrollLeft;
    const targetScroll = Math.round(currentScroll / itemWidth) * itemWidth;

    this.smoothScrollTo(thumbnails, targetScroll);
  }

  smoothScrollTo(thumbnails, targetScroll) {
    const startScroll = thumbnails.scrollLeft;
    const distance = targetScroll - startScroll;
    const duration = 300;
    const startTime = performance.now();

    const animateScroll = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // 使用easeOutCubic缓动函数
      const easeProgress = 1 - Math.pow(1 - progress, 3);

      thumbnails.scrollLeft = startScroll + distance * easeProgress;

      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      }
    };

    requestAnimationFrame(animateScroll);
  }

  setupScrollIndicators() {
    const carousels = document.querySelectorAll(
      ".complementary-thumbnail-carousel"
    );

    carousels.forEach((carousel) => {
      const thumbnails = carousel.querySelector(".complementary-thumbnails");
      if (!thumbnails) return;

      // 创建滚动指示器
      this.createScrollIndicators(carousel, thumbnails);

      // 监听滚动事件更新指示器
      thumbnails.addEventListener("scroll", () => {
        this.updateScrollIndicators(carousel, thumbnails);
      });
    });
  }

  createScrollIndicators(carousel, thumbnails) {
    // 移除现有的指示器
    const existingIndicators = carousel.querySelector(".scroll-indicators");
    if (existingIndicators) {
      existingIndicators.remove();
    }

    const items = thumbnails.querySelectorAll(".complementary-thumbnail-item");
    if (items.length <= 1) return;

    const indicatorsContainer = document.createElement("div");
    indicatorsContainer.className = "scroll-indicators";
    indicatorsContainer.style.cssText = `
      position: absolute;
      bottom: -20px;
      left: 50%;
      transform: translateX(-50%);
      display: flex;
      gap: 6px;
      z-index: 2;
    `;

    const maxVisible = Math.ceil(
      thumbnails.clientWidth / (items[0].offsetWidth + 16)
    );
    const totalPages = Math.ceil(items.length / maxVisible);

    for (let i = 0; i < totalPages; i++) {
      const indicator = document.createElement("div");
      indicator.className = "scroll-indicator";
      indicator.style.cssText = `
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: #ddd;
        transition: background-color 0.3s ease;
        cursor: pointer;
      `;

      indicator.addEventListener("click", () => {
        const targetScroll = i * maxVisible * (items[0].offsetWidth + 16);
        this.smoothScrollTo(thumbnails, targetScroll);
      });

      indicatorsContainer.appendChild(indicator);
    }

    carousel.appendChild(indicatorsContainer);
    this.updateScrollIndicators(carousel, thumbnails);
  }

  updateScrollIndicators(carousel, thumbnails) {
    const indicators = carousel.querySelectorAll(".scroll-indicator");
    if (indicators.length === 0) return;

    const items = thumbnails.querySelectorAll(".complementary-thumbnail-item");
    const maxVisible = Math.ceil(
      thumbnails.clientWidth / (items[0].offsetWidth + 16)
    );
    const currentPage = Math.round(
      thumbnails.scrollLeft / (maxVisible * (items[0].offsetWidth + 16))
    );

    indicators.forEach((indicator, index) => {
      if (index === currentPage) {
        indicator.style.background = "#007acc";
      } else {
        indicator.style.background = "#ddd";
      }
    });
  }

  handleResize() {
    // 重新设置滚动指示器
    this.setupScrollIndicators();
  }
}

// 初始化
document.addEventListener("DOMContentLoaded", () => {
  new ComplementaryMobileCarousel();
});

// 导出类供其他模块使用
if (typeof module !== "undefined" && module.exports) {
  module.exports = ComplementaryMobileCarousel;
}
